import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DateField } from "@/components/dateFields";
import {
  AlertCircle,
  Ban,
  Gift,
  ShieldCheck,
  Building2,
  Infinity as InfinityIcon,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";

import {
  cancelSubscription,
  issueGoodwillCredit,
  setAccessStateOverride,
  setBespoke,
  setNeverExpires,
  formatMoney,
  coerceAccessState,
  type PlatformBillingRowView,
} from "../../../api/General/PlatformBilling";
import { formatDate } from "./helpers";
import { formatLocalDateParam } from '@/utils/apiDate';

/*
 * Every dialog in this file is an OVERRIDE dialog, and each says so in its own words rather than
 * relying on a shared banner nobody reads. Three rules hold across all four:
 *
 *   1. A REASON IS MANDATORY and the field is validated on blur, not on submit — an override with no
 *      recorded reason is indistinguishable from a mistake six months later.
 *   2. NO DIALOG COLLECTS AN AMOUNT THE SERVER DID NOT COMPUTE, except goodwill credit, which is by
 *      definition an operator decision.
 *   3. The destructive submit names the OUTCOME, never the dialog title.
 */

/* ───────────────────────── shared field primitives ───────────────────────── */

export const fieldBase =
  "flex w-full rounded-md border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground " +
  "focus-visible:outline-none focus-visible:ring-2 transition-colors disabled:opacity-50";

export function fieldClass(invalid: boolean): string {
  return `${fieldBase} ${
    invalid
      ? "border-destructive focus-visible:ring-destructive/30"
      : "border-input focus-visible:ring-primary/20 focus-visible:border-primary"
  }`;
}

export function ReasonField({
  id,
  value,
  onChange,
  invalid,
  onBlur,
  hint,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  invalid: boolean;
  onBlur: () => void;
  hint: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground" htmlFor={id}>
        {t("Reason")} <span className="text-destructive">*</span>
      </label>
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={hint}
        className={fieldClass(invalid)}
      />
      {invalid ? (
        <p className="text-xs text-destructive">{t("A reason is required for every override.")}</p>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t("Recorded against this company and shown to the customer.")}
        </p>
      )}
    </div>
  );
}

export function ErrorNote({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
      <AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export function DialogIcon({
  tone,
  children,
}: {
  tone: "danger" | "neutral" | "amber";
  children: React.ReactNode;
}) {
  const tones = {
    danger: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
    amber: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
    neutral: "bg-muted text-foreground",
  } as const;
  return (
    <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${tones[tone]}`}>
      {children}
    </div>
  );
}

export function firstError(errors: { description?: string; code?: string }[] | undefined): string | null {
  if (!errors || errors.length === 0) return null;
  return errors[0]?.description || errors[0]?.code || null;
}

export interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: PlatformBillingRowView | null;
  onSuccess: () => void;
}

/* ═══════════════════════ 1. Issue account credit ═══════════════════════ */

export function IssueCreditDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount("");
      setReason("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const parsed = Number(amount);
  const amountInvalid = !amount || Number.isNaN(parsed) || parsed <= 0;
  const reasonInvalid = reason.trim().length === 0;
  const currency = row.currency || "AED";

  const submit = async () => {
    setTouched(true);
    if (amountInvalid || reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await issueGoodwillCredit(row.companyId, {
        currency,
        amount: parsed,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not issue credit. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Gift size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Issue account credit")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {t(
              "Credit is applied to the next invoice automatically, oldest first. The renewal still runs on schedule and VAT stays correct, so this is the right way to give a customer free time.",
            )}
          </p>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="pb-credit-amount">
              {t("Amount")} ({currency}) <span className="text-destructive">*</span>
            </label>
            <input
              id="pb-credit-amount"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              onBlur={() => setTouched(true)}
              className={`${fieldClass(touched && amountInvalid)} tabular-nums`}
            />
            <p className="text-xs text-muted-foreground">
              {t("Excluding VAT. Credit never converts to cash and never crosses currencies.")}
            </p>
          </div>

          <ReasonField
            id="pb-credit-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. goodwill for the March outage")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting}>
            {amountInvalid
              ? t("Issue credit")
              : t("Issue {{amount}} credit", { amount: formatMoney(parsed, currency) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 2. Cancel (destructive) ═══════════════════════ */

export function CancelSubscriptionDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await cancelSubscription(row.companyId, {
        reason: reason.trim(),
        immediate: true,
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not cancel the subscription. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="danger">
              <Ban size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Cancel subscription")}</DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
            {t(
              "This runs the same refund calculation the customer's own cancellation runs, issues a credit note, and refunds whatever is owed. The amount is computed by the system; you cannot change it.",
            )}
          </div>

          {/* The data promise, restated at the moment it matters most. */}
          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {t(
              "Nothing is deleted. The company keeps read access and export stays available at every stage. They can re-subscribe at any time with no reactivation fee.",
            )}
          </p>

          <ReasonField
            id="pb-cancel-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. customer requested by email on 3 August")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Keep subscription")}
          </Button>
          {/* Names the OUTCOME, not the dialog title. */}
          <Button variant="destructive" onClick={submit} loading={submitting}>
            {t("Cancel and refund what is owed")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 3. Bespoke on / off ═══════════════════════ */

export function BespokeDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [rate, setRate] = useState("");
  const [seats, setSeats] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setRate("");
      setSeats("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const turningOn = !row.operatorManaged;
  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await setBespoke(row.companyId, {
        operatorManaged: turningOn,
        reason: reason.trim(),
        negotiatedRatePerSeatPerMonth: rate ? Number(rate) : null,
        committedSeats: seats ? Number(seats) : null,
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not change Bespoke status. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="neutral">
              <Building2 size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>
                {turningOn ? t("Convert to manual invoicing") : t("Return to self-serve billing")}
              </DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <p className="text-sm text-muted-foreground max-w-[62ch]">
            {turningOn
              ? t(
                  "No card will ever be charged. Renewals and dunning stop. Invoices are still generated, numbered and downloadable by the customer, and seat changes are recorded and priced for you to invoice manually.",
                )
              : t(
                  "The billing engine takes over again. The customer will need a card on file before anything can be charged.",
                )}
          </p>

          {turningOn && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="pb-bespoke-rate">
                  {t("Negotiated rate per seat / month")}
                </label>
                <input
                  id="pb-bespoke-rate"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder={t("Leave blank to keep current")}
                  className={`${fieldClass(false)} tabular-nums`}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="pb-bespoke-seats">
                  {t("Committed seats")}
                </label>
                <input
                  id="pb-bespoke-seats"
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={seats}
                  onChange={(e) => setSeats(e.target.value)}
                  placeholder={t("Leave blank to keep current")}
                  className={`${fieldClass(false)} tabular-nums`}
                />
              </div>
            </div>
          )}

          <ReasonField
            id="pb-bespoke-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. signed enterprise agreement, pays by transfer")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting}>
            {turningOn ? t("Switch to manual invoicing") : t("Hand back to self-serve")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 4. Time-boxed access grant ═══════════════════════ */

export function AccessOverrideDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [until, setUntil] = useState("");
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Default to two weeks out: long enough to resolve a payment problem, short enough that a
      // forgotten override expires on its own rather than becoming a permanent second writer.
      const d = new Date();
      d.setDate(d.getDate() + 14);
      setUntil(formatLocalDateParam(d));
      setReason("");
      setTouched(false);
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const reasonInvalid = reason.trim().length === 0;
  const untilInvalid = !until || new Date(`${until}T23:59:59Z`).getTime() <= Date.now();
  const currentAccess = coerceAccessState(row.accessState);

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid || untilInvalid) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await setAccessStateOverride(row.companyId, {
        // 0 = Full. An override may only ever GRANT; the server refuses anything less generous.
        state: 0,
        untilUtc: new Date(`${until}T23:59:59Z`).toISOString(),
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(t("Could not apply the override. Try again."));
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone="amber">
              <ShieldCheck size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>{t("Grant full access temporarily")}</DialogTitle>
              <DialogDescription className="mt-1">
                {row.companyName}
                {currentAccess && (
                  <>
                    <span className="mx-1.5 text-muted-foreground/50">·</span>
                    <span className="text-xs uppercase tracking-wider">{t(currentAccess)}</span>
                  </>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
            {t(
              "An override can only give access, never take it away, and it does not stop any charge from running on schedule. The company keeps appearing in the drift report until it expires or they pay.",
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground" htmlFor="pb-override-until">
              {t("Expires on")} <span className="text-destructive">*</span>
            </label>
            <DateField
              id="pb-override-until"
              value={until}
              onChange={(v) => setUntil(v)}
              onBlur={() => setTouched(true)}
              inputClassName={touched && untilInvalid ? "border-destructive focus-visible:ring-destructive/30" : undefined}
            />
            {touched && untilInvalid ? (
              <p className="text-xs text-destructive">{t("Pick a date in the future.")}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t(
                  "Access reverts on its own when this date passes. To keep a company running indefinitely, set it to never expire instead.",
                )}
              </p>
            )}
          </div>

          <ReasonField
            id="pb-override-reason"
            value={reason}
            onChange={setReason}
            invalid={touched && reasonInvalid}
            onBlur={() => setTouched(true)}
            hint={t("e.g. bank transfer in flight, confirmed with finance")}
          />

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting}>
            {t("Grant access until {{date}}", { date: until || "—" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ═══════════════════════ 5. Perpetual entitlement (never expires) ═══════════════════════ */

/**
 * SET or CLEAR the perpetual flag.
 *
 * Why this one is shaped differently from the other four:
 *
 * • SETTING it is an open-ended commercial commitment with no end date to walk it back — so it is a
 *   TWO-STEP dialog. Step one explains and takes the reason; step two restates the company name and
 *   requires an explicit acknowledgement. A single toggle would let an operator commit the business
 *   to a perpetual free tenant with one mis-click on a row menu.
 * • CLEARING it is the only direction that can REDUCE a customer's entitlement — the tenant goes
 *   straight back onto the dunning ladder and can be restricted by the next job run. That path gets
 *   the destructive submit and says what will resume, in one step (a confirmation gate on the
 *   direction that RESTORES normal behaviour would just be friction).
 * • It is deliberately NOT described as an "override". An override expires; this does not. Sharing
 *   the vocabulary is how an operator ends up believing this will lapse on its own.
 */
export function NeverExpiresDialog({ open, onOpenChange, row, onSuccess }: BaseProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [step, setStep] = useState<"reason" | "confirm">("reason");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
      setAcknowledged(false);
      setStep("reason");
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  if (!row) return null;

  const turningOn = !row.neverExpires;
  const reasonInvalid = reason.trim().length === 0;

  const submit = async () => {
    setTouched(true);
    if (reasonInvalid) return;
    // Setting it goes through the acknowledgement step first; clearing it does not.
    if (turningOn && step === "reason") {
      setStep("confirm");
      return;
    }
    if (turningOn && !acknowledged) return;

    setSubmitting(true);
    setError(null);
    try {
      const result = await setNeverExpires(row.companyId, {
        neverExpires: turningOn,
        reason: reason.trim(),
      });
      const message = firstError(result?.errors);
      if (message) {
        setError(message);
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch {
      setError(
        turningOn
          ? t("Could not make this subscription perpetual. Try again.")
          : t("Could not put this subscription back on the normal schedule. Try again."),
      );
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={submitting ? undefined : onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <DialogIcon tone={turningOn ? "neutral" : "amber"}>
              <InfinityIcon size={18} aria-hidden="true" />
            </DialogIcon>
            <div className="min-w-0">
              <DialogTitle>
                {turningOn
                  ? t("Keep this company running indefinitely")
                  : t("Put this company back on the normal schedule")}
              </DialogTitle>
              <DialogDescription className="mt-1">{row.companyName}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-5">
          {/*
            The current grant, stated before anything else, whenever one exists. An operator arriving
            from a support call needs "who did this and why" before they decide to undo it.
          */}
          {row.neverExpires && (
            <div className="rounded-md border border-border bg-muted/50 p-3 text-sm">
              <p className="font-medium text-foreground">
                {t("This company is already set to never expire.")}
              </p>
              <dl className="mt-2 space-y-1 text-muted-foreground">
                <div className="flex flex-wrap gap-x-1.5">
                  <dt>{t("Set by")}</dt>
                  <dd className="text-foreground">{row.neverExpiresSetByName || t("Not recorded")}</dd>
                </div>
                <div className="flex flex-wrap gap-x-1.5">
                  <dt>{t("Set on")}</dt>
                  <dd className="text-foreground">{formatDate(row.neverExpiresSetAtUtc)}</dd>
                </div>
                {row.neverExpiresReason && (
                  <div className="flex flex-wrap gap-x-1.5">
                    <dt>{t("Reason given")}</dt>
                    <dd className="text-foreground">{row.neverExpiresReason}</dd>
                  </div>
                )}
              </dl>
            </div>
          )}

          {step === "reason" ? (
            <>
              <p className="text-sm text-muted-foreground max-w-[62ch]">
                {turningOn
                  ? t(
                      "Nothing in the product will expire this company again. Trials stop converting to Free, unpaid terms stop restricting access, and dunning stops taking anything away. Invoices are still issued and charges still run on schedule — only the loss of access is switched off.",
                    )
                  : t(
                      "Trial expiry, renewal and dunning start applying to this company again. If a term has already ended or a charge has already failed, access can be reduced the next time those jobs run — possibly within the hour.",
                    )}
              </p>

              <p className="text-sm text-muted-foreground max-w-[62ch]">
                {turningOn
                  ? t(
                      "This has no end date. It stays in force until an operator turns it off here, and it applies whether the customer pays us themselves or we invoice them.",
                    )
                  : t("You can set it back to never expire at any time.")}
              </p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground" htmlFor="pb-never-reason">
                  {t("Reason")} <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="pb-never-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder={
                    turningOn
                      ? t("e.g. founding customer, perpetual access agreed with the CEO")
                      : t("e.g. agreement ended, back to standard terms from September")
                  }
                  className={fieldClass(touched && reasonInvalid)}
                />
                {touched && reasonInvalid ? (
                  <p className="text-xs text-destructive">
                    {t("A reason is required for every override.")}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "Recorded on the subscription history and sent to the operator team. The customer never sees it.",
                    )}
                  </p>
                )}
              </div>
            </>
          ) : (
            /* Step two. Restates the commitment in the plainest words available. */
            <>
              <p className="text-sm text-foreground max-w-[62ch]">
                {t(
                  "{{company}} will keep working with no expiry date, indefinitely, until an operator turns this off.",
                  { company: row.companyName },
                )}
              </p>
              <div className="rounded-md border border-border bg-muted/50 p-3">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t("Reason")}
                </p>
                <p className="mt-1 text-sm text-foreground">{reason.trim()}</p>
              </div>
              <label className="flex items-start gap-2.5 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                />
                <span className="max-w-[58ch]">
                  {t("I understand this commitment has no end date and only an operator can end it.")}
                </span>
              </label>
            </>
          )}

          <ErrorNote message={error} />
        </DialogBody>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => (step === "confirm" ? setStep("reason") : onOpenChange(false))}
            disabled={submitting}
          >
            {step === "confirm" ? t("Back") : t("Cancel")}
          </Button>
          {turningOn ? (
            <Button onClick={submit} loading={submitting} disabled={step === "confirm" && !acknowledged}>
              {step === "reason" ? t("Review this commitment") : t("Set to never expire")}
            </Button>
          ) : (
            /* Clearing is the direction that can take entitlement away. */
            <Button variant="destructive" onClick={submit} loading={submitting}>
              {t("Resume normal expiry")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
